import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CustomBadge } from "@/lib/badges";

const SETTINGS_KEY = "custom_film_badges";

let cache: CustomBadge[] | null = null;
let pending: Promise<CustomBadge[]> | null = null;
const listeners = new Set<(v: CustomBadge[]) => void>();

function sanitize(raw: unknown): CustomBadge[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r: any) => {
      if (!r || typeof r.label !== "string") return null;
      return {
        label: String(r.label),
        bg: typeof r.bg === "string" ? r.bg : "#1a1a2e",
        fg: typeof r.fg === "string" ? r.fg : "#ffffff",
        border: typeof r.border === "string" ? r.border : "#ffffff33",
        glow: !!r.glow,
      } as CustomBadge;
    })
    .filter((b): b is CustomBadge => b !== null);
}

export async function loadCustomBadges(force = false): Promise<CustomBadge[]> {
  if (!force && cache) return cache;
  if (!force && pending) return pending;
  pending = (async () => {
    const { data } = await (supabase as any)
      .from("site_settings")
      .select("value")
      .eq("key", SETTINGS_KEY)
      .maybeSingle();
    const arr = sanitize(data?.value);
    cache = arr;
    listeners.forEach((l) => l(arr));
    return arr;
  })();
  try {
    return await pending;
  } finally {
    pending = null;
  }
}

export async function saveCustomBadges(next: CustomBadge[]): Promise<void> {
  const clean = sanitize(next);
  const { error } = await (supabase as any)
    .from("site_settings")
    .upsert({ key: SETTINGS_KEY, value: clean });
  if (error) throw error;
  cache = clean;
  listeners.forEach((l) => l(clean));
}

export function useCustomBadges(): CustomBadge[] {
  const [badges, setBadges] = useState<CustomBadge[]>(cache ?? []);
  useEffect(() => {
    listeners.add(setBadges);
    if (!cache) {
      loadCustomBadges().catch(() => {
        /* silent */
      });
    } else {
      setBadges(cache);
    }
    return () => {
      listeners.delete(setBadges);
    };
  }, []);
  return badges;
}